/**
 * Host side of the Form.io token refresh contract (SE-9160).
 *
 * The scoped Form.io token in the renderer URL is minted once, when the form
 * opens, and can expire while a scout is still typing (SE-9157). The renderer
 * (gemini-formio-renderer, SE-9159) then asks its host for a fresh token
 * instead of failing the submit. Every message is a JSON string:
 *
 *   Request, renderer → host:
 *     {"type":"formio_token_refresh_request","payload":{"formId":"<id>","requestId":"<unique>"}}
 *
 *   Reply, host → renderer, one of:
 *     {"type":"formio_token_refresh","payload":{"requestId":"<same>","token":"<jwt>"}}
 *     {"type":"formio_token_refresh_failed","payload":{"requestId":"<same>"}}
 *
 * The renderer accepts a reply only when `event.source === window.parent`
 * and the `requestId` matches, and gives up after 10 s. A failed reply makes
 * it show its reopen guidance at once instead of waiting out that timeout.
 *
 * The success reply carries a credential, so nothing here logs it, and the
 * callers must never post it to a wildcard origin outside the renderer's own
 * top frame.
 * @module features/player-intake/utils/tokenRefresh
 */

const TOKEN_REFRESH_REQUEST_TYPE = "formio_token_refresh_request";
const TOKEN_REFRESH_REPLY_TYPE = "formio_token_refresh";
export const TOKEN_REFRESH_FAILED_TYPE = "formio_token_refresh_failed";

/** The host's answer to one refresh request. */
export type TokenRefreshReply =
  | {
      readonly type: typeof TOKEN_REFRESH_REPLY_TYPE;
      readonly payload: { readonly requestId: string; readonly token: string };
    }
  | {
      readonly type: typeof TOKEN_REFRESH_FAILED_TYPE;
      readonly payload: { readonly requestId: string };
    };

/**
 * What the host should do with a message the native bridge relayed.
 * `none` — not part of the refresh contract, handle as before.
 * `ignore` — part of it, but must not be answered or logged.
 * `answer` — a refresh request from the renderer's top frame.
 */
type BridgeTokenRefreshAction =
  | { readonly kind: "none" }
  | { readonly kind: "ignore" }
  | {
      readonly kind: "answer";
      readonly requestId: string;
      /** The form origin the reply must be targeted at. */
      readonly targetOrigin: string;
    };

/** A message relayed by the native bridge (`formBridgeScript`), as parsed. */
interface BridgeEnvelope {
  readonly type?: unknown;
  readonly payload?: unknown;
  /** Whether the renderer's top-level document posted the inner message. */
  readonly fromTopFrame?: unknown;
  /** The origin the inner message was posted from. */
  readonly origin?: unknown;
}

/** A loosely-typed renderer message, as parsed off the wire. */
interface WireMessage {
  readonly type?: unknown;
  readonly payload?: unknown;
}

const asWireMessage = (message: unknown): WireMessage | null =>
  message !== null && typeof message === "object"
    ? (message as WireMessage)
    : null;

/**
 * Whether a message belongs to the refresh contract at all — a request or
 * either reply. On native the bridge already filters the host's own injected
 * replies out; recognising them here is the host's second guard, so a reply
 * that ever got past the bridge is still dropped unanswered and unlogged.
 * @param message - A parsed renderer message.
 * @returns True for any of the three contract message types.
 */
export const isTokenRefreshMessage = (message: unknown): boolean => {
  const type = asWireMessage(message)?.type;
  return (
    type === TOKEN_REFRESH_REQUEST_TYPE ||
    type === TOKEN_REFRESH_REPLY_TYPE ||
    type === TOKEN_REFRESH_FAILED_TYPE
  );
};

/**
 * The `requestId` of a refresh request, or null when the message is not one.
 * A request without a usable `requestId` cannot be matched by the renderer,
 * so it is treated as no request at all rather than answered blind.
 * @param message - A parsed renderer message.
 * @returns The request id, or null.
 */
export const tokenRefreshRequestId = (message: unknown): string | null => {
  const wire = asWireMessage(message);
  if (wire?.type !== TOKEN_REFRESH_REQUEST_TYPE) return null;
  const payload = asWireMessage(wire.payload) as {
    readonly requestId?: unknown;
  } | null;
  const requestId = payload?.requestId;
  return typeof requestId === "string" && requestId ? requestId : null;
};

/**
 * The `token` query param of a renderer URL, or null when the URL is missing,
 * will not parse, or carries no token.
 * @param url - The renderer URL `getIntakeFormUrl` returned.
 * @returns The token, or null.
 */
export const tokenFromFormUrl = (
  url: string | null | undefined
): string | null => {
  if (!url) return null;
  try {
    return new URL(url).searchParams.get("token") || null;
  } catch {
    return null;
  }
};

/**
 * Builds the reply to a refresh request from a freshly minted renderer URL.
 * Any URL that yields no token produces the failed reply, never a token-less
 * success.
 * @param requestId - The id the renderer sent.
 * @param freshUrl - The renderer URL the refetch returned, if any.
 * @returns The reply to post back to the renderer.
 */
export const buildTokenRefreshReply = (
  requestId: string,
  freshUrl: string | null | undefined
): TokenRefreshReply => {
  const token = tokenFromFormUrl(freshUrl);
  return token
    ? { type: TOKEN_REFRESH_REPLY_TYPE, payload: { requestId, token } }
    : { type: TOKEN_REFRESH_FAILED_TYPE, payload: { requestId } };
};

/**
 * The script that delivers a reply inside the native WebView. Dispatching it
 * from the page itself makes `event.source === window`, which is what the
 * renderer (the top frame on native, so `window.parent === window`) accepts;
 * react-native-webview's own `postMessage()` arrives with a null source and is
 * ignored. The double stringify yields a JS string literal of the JSON message.
 *
 * SE-9160 deliberately tightens the SE-9159 review's `"*"` target to the form
 * origin: a same-window post with a specific target still arrives with
 * `source === window`, and if the top frame has navigated elsewhere between
 * the request and the reply, the browser drops the message instead of handing
 * the token to that page.
 * @param reply - The reply to deliver.
 * @param targetOrigin - The form's origin.
 * @returns A script for `WebView.injectJavaScript`.
 */
export const buildNativeReplyScript = (
  reply: TokenRefreshReply,
  targetOrigin: string
): string =>
  `window.postMessage(${JSON.stringify(JSON.stringify(reply))}, ${JSON.stringify(targetOrigin)}); true;`;

/** Where a native bridge message came from. */
interface BridgeSenders {
  /** The origin of the form URL, or null when it has none. */
  readonly formOrigin: string | null;
  /**
   * The origin of the URL react-native-webview reports for the message
   * (`nativeEvent.url`). Unlike the envelope's fields, the page cannot write
   * it, so it is what actually pins the sender to the form.
   */
  readonly senderOrigin: string | null;
}

/**
 * Decides how to treat a native bridge envelope. A refresh request is answered
 * only when the WebView itself reports the form's origin and the bridge saw
 * the request come from the renderer's top-level document (`fromTopFrame`) at
 * that origin: the bridge relays messages from every child frame too, and a
 * token must never go back to one of those.
 * @param envelope - The parsed bridge message.
 * @param senders - The form's origin and the WebView-reported sender origin.
 * @returns The action to take.
 */
export const bridgeTokenRefreshAction = (
  envelope: BridgeEnvelope,
  senders: BridgeSenders
): BridgeTokenRefreshAction => {
  if (envelope.type !== "formio_message") return { kind: "none" };
  if (!isTokenRefreshMessage(envelope.payload)) return { kind: "none" };
  const requestId = tokenRefreshRequestId(envelope.payload);
  const { formOrigin, senderOrigin } = senders;
  const isFromForm =
    formOrigin !== null &&
    senderOrigin === formOrigin &&
    envelope.fromTopFrame === true &&
    envelope.origin === formOrigin;
  return requestId && isFromForm
    ? { kind: "answer", requestId, targetOrigin: formOrigin }
    : { kind: "ignore" };
};
